<script lang="ts">
  import type { Snippet } from "svelte";
  interface Props {
    tone: "info" | "ok" | "warn" | "degraded" | "error";
    title: string;
    correlationId?: string | undefined;
    /** `alert` for errors (assertive), `status` for informational (polite). */
    role?: "alert" | "status";
    actions?: Snippet;
    children?: Snippet;
  }
  const { tone, title, correlationId, role, actions, children }: Props = $props();
  const resolvedRole = $derived(role ?? (tone === "error" ? "alert" : "status"));
</script>

<div
  class="banner tone-{tone}"
  role={resolvedRole}
  aria-live={resolvedRole === "alert" ? "assertive" : "polite"}
>
  <div class="banner-body">
    <strong class="banner-title">{title}</strong>
    {#if children}<div class="banner-text">{@render children()}</div>{/if}
    {#if correlationId}<p class="ref mono">Reference: {correlationId}</p>{/if}
  </div>
  {#if actions}<div class="banner-actions">{@render actions()}</div>{/if}
</div>

<style>
  .banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    border-radius: var(--radius);
    padding: 16px 18px;
    border: 1px solid var(--line-soft);
    background: var(--surface);
    font-size: 14px;
  }
  .banner-title {
    color: var(--heading);
    font-weight: 700;
    display: block;
  }
  .banner-text {
    margin-top: 6px;
    color: var(--text);
  }
  .ref {
    margin-top: 8px;
    font-size: 12px;
    color: var(--muted);
  }
  .banner-actions {
    display: flex;
    gap: 8px;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
  }
  .tone-ok {
    border-color: var(--accent-border);
    background: var(--accent-fill);
  }
  .tone-info {
    border-color: var(--line);
    background: var(--accent-haze);
  }
  .tone-warn,
  .tone-degraded {
    border-color: var(--amber-border);
    background: var(--amber-fill);
  }
  .tone-error {
    border-color: var(--err-border);
    background: var(--err-fill);
  }
  @media (max-width: 560px) {
    .banner {
      flex-direction: column;
    }
  }
</style>
